import React from 'react';
import { CheckCircle2, Circle } from 'lucide-react';

interface KittingProgressRingProps {
  completionPct: number;
  totalScanned: number;
  totalRequired: number;
  completedItems: number;
  totalItems: number;
}

export default function KittingProgressRing({
  completionPct,
  totalScanned,
  totalRequired,
  completedItems,
  totalItems,
}: KittingProgressRingProps) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (completionPct / 100) * circumference;

  return (
    <div className="card-elevated p-5">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
        Kit Assembly Progress
      </p>
      <div className="flex items-center gap-5">
        {/* Ring */}
        <div className="relative flex-shrink-0">
          <svg width="128" height="128" viewBox="0 0 128 128">
            <circle
              cx="64"
              cy="64"
              r={radius}
              className="progress-ring-track"
            />
            <circle
              cx="64"
              cy="64"
              r={radius}
              className="progress-ring-fill"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 64 64)"
              stroke="var(--primary)"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold text-foreground tabular-nums">{completionPct}%</span>
            <span className="text-2xs text-muted-foreground">complete</span>
          </div>
        </div>

        {/* Stats */}
        <div className="flex-1 space-y-3">
          <div>
            <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-0.5">Units Scanned</p>
            <p className="text-xl font-bold text-foreground tabular-nums">
              {totalScanned}{' '}
              <span className="text-sm font-normal text-muted-foreground">/ {totalRequired}</span>
            </p>
          </div>
          <div>
            <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-0.5">Items Complete</p>
            <p className="text-xl font-bold text-foreground tabular-nums">
              {completedItems}{' '}
              <span className="text-sm font-normal text-muted-foreground">/ {totalItems}</span>
            </p>
          </div>
          {/* Mini item status */}
          <div className="flex flex-wrap gap-1">
            {Array.from({ length: totalItems }).map((_, i) => (
              <div
                key={`item-dot-${i}`}
                className={`w-5 h-5 rounded flex items-center justify-center text-2xs ${
                  i < completedItems
                    ? 'bg-primary/20 text-primary' :'bg-muted text-muted-foreground'
                }`}
              >
                {i < completedItems ? (
                  <CheckCircle2 size={12} />
                ) : (
                  <Circle size={12} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-4">
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </div>
    </div>
  );
}